import { DEFAULT_BASEMAP_LAYER_ID, isBasemapLayer, isLayerBlocked, isLayerInMenu } from '../layer-access'

// The full view's scratchpad map keeps its own layers, apart from the main map's (which the ARTEMIS tab
// hides): { basemapId, overlayIds }. It starts as a copy of the main map's, then changes on its own: from
// its layers menu, and from Artemis's set_layer_visibility while the full view is open. Kept per
// conversation in this browser, since map layers are each viewer's own, even in a shared session.

// Google's own basemaps, as the main map draws them (map-types imageryAndTopographyGoogle).
export const GOOGLE_BASEMAP_TYPES = { 81: 'hybrid', 891: 'hybrid', 82: 'terrain' }

// The main map's SAT and HYB buttons (map/layer-toggle). They aren't layer-menu entries (891 is a stand-in
// the app adds to the catalog itself), so they're always offered and always allowed. HYB is satellite plus
// its companion overlay, topo lines (overlayLayerId).
export const TOGGLE_BASEMAPS = [{ id: 81, name: 'Satellite' }, { id: 891, name: 'Hybrid' }]
const isToggleBasemap = id => TOGGLE_BASEMAPS.some(b => b.id === id)

// What the scratchpad map draws over its basemap: its overlays, and the basemap's companion (HYB's topo lines).
export const drawnOverlayIds = (layers, layersDictionary = {}) => {
  const companion = layersDictionary[layers.basemapId] && layersDictionary[layers.basemapId].overlayLayerId
  return companion && !layers.overlayIds.includes(companion) ? [companion, ...layers.overlayIds] : layers.overlayIds
}

export const initialScratchLayers = (activeLayers = [], layersDictionary = {}) => {
  const layers = activeLayers.map(id => layersDictionary[id]).filter(Boolean)
  const basemap = layers.find(isBasemapLayer)
  return {
    basemapId: basemap ? basemap.id : DEFAULT_BASEMAP_LAYER_ID,
    overlayIds: layers.filter(l => !isBasemapLayer(l)).map(l => l.id)
  }
}

const key = conversationId => `artemis.scratchLayers.${conversationId}`

const valid = v => !!v && Number.isFinite(v.basemapId) && Array.isArray(v.overlayIds) && v.overlayIds.every(Number.isFinite)

// A conversation's saved scratchpad layers, or null (none saved, a private window, or junk).
export const readScratchLayers = conversationId => {
  if (!conversationId) return null
  try {
    const value = JSON.parse(window.localStorage.getItem(key(conversationId)) || 'null')
    return valid(value) ? { basemapId: value.basemapId, overlayIds: value.overlayIds } : null
  } catch (e) {
    return null
  }
}

export const writeScratchLayers = (conversationId, layers) => {
  if (!conversationId || !valid(layers)) return
  try { window.localStorage.setItem(key(conversationId), JSON.stringify(layers)) } catch (e) { /* storage full or off */ }
}

// set_layer_visibility on the scratchpad map, with the layer menu's rules (as runSetLayerVisibility
// applies them to the main map). → { outcome, layerId, layerName, visible, next }; next is the new
// layers when the outcome is 'changed'.
export const requestScratchLayer = (layers, input, { layersDictionary = {}, userSubscriptions = [], productionLayers = false } = {}) => {
  const layerId = Number(input && input.layer_id),
    visible = !!(input && input.visible),
    toggle = isToggleBasemap(layerId),
    layer = layersDictionary[layerId] || (toggle ? { id: layerId, layerNodeId: 5 } : null),
    layerName = (toggle && TOGGLE_BASEMAPS.find(b => b.id === layerId).name) || (layer && layer.name) || (input && input.layer_name) || `Layer ${input && input.layer_id}`,
    result = (outcome, next = null) => ({ outcome, layerId, layerName, visible, next })

  if (!layer || (!toggle && !isLayerInMenu(layer, userSubscriptions, productionLayers))) return result('not_found')
  const basemap = toggle || isBasemapLayer(layer)
  const active = basemap ? layers.basemapId === layerId : layers.overlayIds.includes(layerId)
  if (active === visible) return result('unchanged')
  if (visible && !toggle && isLayerBlocked(layer, userSubscriptions)) return result('subscription_required')
  // Basemaps are replaced, not turned off (as on the main map).
  if (!visible && basemap) return result('invalid_input')

  const next = basemap
    ? { ...layers, basemapId: layerId }
    : { ...layers, overlayIds: visible ? [...layers.overlayIds, layerId] : layers.overlayIds.filter(id => id !== layerId) }
  return result('changed', next)
}

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name))

// The layers menu: the basemaps the user can pick, and overlays to toggle: those on the scratchpad map,
// then those on the main map, each once.
export const scratchLayerChoices = (layers, activeLayers = [], layersDictionary = {}, userSubscriptions = [], productionLayers = false) => {
  const usable = l => l && isLayerInMenu(l, userSubscriptions, productionLayers) && !isLayerBlocked(l, userSubscriptions)
  const basemaps = [
    ...TOGGLE_BASEMAPS,
    ...Object.values(layersDictionary).filter(l => usable(l) && isBasemapLayer(l) && !isToggleBasemap(l.id)).sort(byOrder)
  ].map(l => ({ id: l.id, name: l.name, on: l.id === layers.basemapId }))
  const ids = [...new Set([...layers.overlayIds, ...activeLayers])]
  const overlays = ids.map(id => layersDictionary[id]).filter(l => usable(l) && !isBasemapLayer(l))
    .map(l => ({ id: l.id, name: l.name, on: layers.overlayIds.includes(l.id) }))
  return { basemaps, overlays }
}
